import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { iif, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';

import { AARoutePlannerEvents, RouteValidationMessage } from 'src/app/core/events/events';
import { AAConstant, AppError } from 'src/app/core/models/constants';
import { AppUtilityService } from 'src/app/core/services/app-utility/app-utility.service';
import { EventsService } from 'src/app/core/services/events.service';
import { AppSettings } from 'src/app/core/shared';
import { AALocationModel } from 'src/app/modules/route-planner/models/route-model';
import { AppService } from '../../services/app-service/app.service';
import { GoogleMapHTTPService } from '../../services/google/google-map-http/google-map-http.service';
import { LocationService } from '../../services/location-service/location.service';

@Component({
    selector: 'app-google-place-search',
    templateUrl: './google-place-search.component.html',
    styleUrls: ['./google-place-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class GooglePlaceSearchComponent implements OnInit, OnDestroy, OnChanges {

  constructor(private googleMapHttpService: GoogleMapHTTPService,
    private appService: AppService,
    private eventServices: EventsService,
    private cdr: ChangeDetectorRef,
    private appUtilityService: AppUtilityService,
    private locationService: LocationService ) {
    this.onValueChanged = new EventEmitter();
    this.onBlur = new EventEmitter();
    this.onSubmit = new EventEmitter();
    this.onGotFocus = new EventEmitter();
  }

  @Input() placeHolder = '';
  @Input() title = '';
  @Input() value = '';
  @Input() useLocalStorage = false;
  @Input() showCurrentLocation = false;
  @Input() currentLocation = '';
  @Input() useCurrentLocationIcon = false;
  @Input() useFirstEntryAsDefault = false;
  @Input() locationPlace: any;
  @Input() locationModel: AALocationModel;
  @Input() updateLocationName: string;
  @Input() minCharsEntryForSearch = 0;

  @Output() onValueChanged: EventEmitter<any>;
  @Output() onBlur: EventEmitter<any>;
  @Output() onSubmit: EventEmitter<any>;
  @Output() onGotFocus: EventEmitter<any>;

  @ViewChild('instance', { static: true }) instance: NgbTypeahead;
  @ViewChild('elem', { static: true }) elem: ElementRef;

  APP_GOOGLE_PLACE_NOTFOUND = 'Sorry, suggestions could not be loaded.';
  search: any;
  localStorageValues: any;
  currentSelectionList = [];
  locationSearchTerm = '';
  searching = false;
  searchFailed = false;

  private lastEmitTime: Date = new Date();

  public typeaheadFormatter(data) {
    if (typeof data === 'string') {
      return data;
    }

    return data.description;
  }

  public highlightText($event): void {
    const input: any = $event.target;

    if (input) {
      setTimeout( () => {
        input.focus();
        input.setSelectionRange(0, 999);
      }, 5);
    }
  }

  private setupPlaceHolder = (placeHolder: string) => {
    this.placeHolder = placeHolder;
    setTimeout(() => {
      try {
        this.cdr.detectChanges();
      } catch (error) {}
    }, 10);
  }

  ngOnDestroy(): void {
    this.eventServices.remove(RouteValidationMessage.FROM_TO_MESSAGE, this.setupPlaceHolder);
    this.eventServices.remove(RouteValidationMessage.VIA_MESSAGE, this.setupPlaceHolder);
    this.eventServices.remove(AARoutePlannerEvents.DEDUCTED_CHANGES, this.deductChanges);
  }

  ngOnInit(): void {
    this.eventServices.on(RouteValidationMessage.FROM_TO_MESSAGE, this.setupPlaceHolder);
    this.eventServices.on(RouteValidationMessage.VIA_MESSAGE, this.setupPlaceHolder);
    this.eventServices.on(AARoutePlannerEvents.DEDUCTED_CHANGES, this.deductChanges);

    this.search = (text$: Observable<string>) => {
        return text$.pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            tap(() => {
                this.searching = true;
                this.currentSelectionList = [];
                this.searchFailed = false;
            }),
            finalize(() => {
                this.searching = false;
            }),
            switchMap((term: string) =>
                iif(
                    () => term.length < AAConstant.MIN_CHARS_ENTRY_FOR_SEARCH,
                    of(this.appService.geoDataStore)
                        .pipe(
                            map((data: Array<any>) => {
                                this.currentSelectionList = term.length < 2 ? [] : data
                                    .filter(item => item.description.toLowerCase().indexOf(term.toLowerCase()) > -1)
                                    .slice(0, 5);

                                return this.currentSelectionList;
                            })
                    ),
                    this.googleMapHttpService
                        .getChangeFromPlace(term)
                        .pipe(
                            map(data => {
                                return this.currentSelectionList = data || [];
                            }),
                            catchError(() => {
                                this.searchFailed = true;
                                return of([]);
                            })
                        )
                )
            ),
            catchError(() => {
              this.searchFailed = true;
              return of([]);
            })
        );
    };

    if (this.currentLocation !== '') {
      setTimeout(() => {
        this.setManualLocationPlace(this.currentLocation);
      }, 100);
    }
  }

  onKeydown($event) {
    this.localStorageValues = null;
    switch ($event.code || $event.key || $event.keyCode) {
      case 27:
      case 'Escape':
        this.instance.dismissPopup();
        break;

      case 13:
      case 'NumpadEnter':
      case 'Enter':
        const lastTriggeredInterval = (new Date().getTime() - this.lastEmitTime.getTime()) / (AppSettings.INTERVAL_IN_SECONDS * 1000);
        if (lastTriggeredInterval >= AppSettings.INTERVAL_IN_SECONDS) {
          this.onBlurFocus($event);
          this.onSubmit.emit();
          this.lastEmitTime = new Date();
        }
        break;
    }

  }

  public openTypeahead(): void {
    this.elem.nativeElement.dispatchEvent(new Event('input'));
    this.elem.nativeElement.focus();
  }

  onFocus() {
    this.placeHolder = '';
    this.locationModel.errorMessage = '';
    this.onGotFocus.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (typeof changes['placeHolder'] !== 'undefined') {
      this.placeHolder = changes['placeHolder'].currentValue;
    }

    if (typeof changes['currentLocation'] !== 'undefined') {
      this.setManualLocationPlace(changes['currentLocation'].currentValue);
    }

    if (changes?.updateLocationName) {
        this.locationSearchTerm =  this.locationModel.Name;
    }
  }

  useCurrentLocation() {
    this.locationModel.Name = AAConstant.CURRENT_LOCATION;
    this.locationModel.isLoading = true;

    this.locationService.loadCurrentLocation(this.locationModel).subscribe(r => {
        this.locationModel.isLoading = false;

      if (r.errorMessage === AppError.LOCATION_ACCESS_DENIED_ERR) {
          this.placeHolder = AppError.LOCATION_ACCESS_DENIED;
      } else {
        this.locationModel.Name = r.Name;
        this.onSubmit.emit();
      }
    });
  }

  public setManualLocationPlace(place: string): void {
    this.elem.nativeElement.value = place;
  }

  public onInputHandler(updatedValue) {
    this.locationModel.clear();

    this.locationModel.Name = updatedValue;
  }

  selectedItem(selectedItem: any): void {
    this.locationModel.clear();
    this.locationModel.PredictionList = this.currentSelectionList;

    this.locationModel.Name = selectedItem.description;
    this.locationModel.PlaceId = selectedItem.place_id;
  }

  onBlurFocus($event) {
    if (this.currentSelectionList.length > 0 && this.locationModel.PlaceId === '') {
        const selectedPrediction = this.currentSelectionList[0];

        this.locationModel.clear();

        this.locationModel.Name = selectedPrediction.description;
        this.locationModel.PlaceId = selectedPrediction.place_id;

        this.currentSelectionList = [];
    }
  }

  private deductChanges = () => {
    this.locationSearchTerm = this.locationModel.Name;
    this.placeHolder = this.locationModel.errorMessage;

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 10);
  }

  get isLocationFeature() {
     return (this.locationModel.errorMessage === AppError.LOCATION_ACCESS_FEATURE_TURNON
                && !this.appUtilityService.isDesktop) ? true : false;
  }
}
